import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { HostedAgent } from "@/api/admin-api"

import { getHostedAgentConflict } from "@/api/admin-api"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent } from "@/components/ui/tabs"
import { useRegisterAdminDirty } from "@/features/admin-dashboard/admin-unsaved-changes-context"

import { AccessPolicyDialog } from "./access-policy-dialog"
import { HostedAgentAccessTab } from "./hosted-agent-access-tab"
import { HostedAgentEditConflictAlert } from "./hosted-agent-edit-conflict-alert"
import { HostedAgentEditTabsList } from "./hosted-agent-edit-tabs-list"
import { HostedAgentDiscardDialog, HostedAgentEditorFooter } from "./hosted-agent-editor-footer"
import { HostedAgentEvalsTab } from "./hosted-agent-evals-tab"
import { HostedAgentLibrariesTab } from "./hosted-agent-libraries-tab"
import { HostedAgentLifecyclePanel } from "./hosted-agent-lifecycle-panel"
import { HostedAgentModelTab } from "./hosted-agent-model-tab"
import { HostedAgentPublishTab } from "./hosted-agent-publish-tab"
import { HostedAgentRoutinesTab } from "./hosted-agent-routines-tab"
import { HostedAgentSelftestPanel } from "./hosted-agent-selftest-panel"
import { HostedAgentSourcesTab } from "./hosted-agent-sources-tab"
import { HostedAgentToolsTab } from "./hosted-agent-tools-tab"
import { HostedAgentUsagePanel } from "./hosted-agent-usage-panel"
import {
  HostedAgentBudgetTab,
  HostedAgentInstructionsTab,
  HostedAgentMemoryTab,
  HostedAgentProfileTab,
  HostedAgentSkillsTab,
} from "./hosted-agent-wizard-tabs"
import { HostedAgentWorkingStatePanel } from "./hosted-agent-working-state-panel"
import { RawJsonTab } from "./shared/raw-json-tab"
import {
  useAdminHostedAgentQuery,
  useAdminHostedAgentRawQuery,
  useAdminHostedAgentToolCatalogQuery,
  useAdminModelsQuery,
  useSaveHostedAgentRawMutation,
  useUpdateHostedAgentAccessMutation,
  useUpdateHostedAgentMutation,
} from "../hooks/admin-queries"
import { HOSTED_AGENT_STEPS } from "../utils/hosted-agent-setup"
import {
  buildHostedAgentFormState,
  buildHostedAgentSavePayload,
  firstInvalidHostedAgentTab,
  type HostedAgentFormErrors,
  type HostedAgentFormState,
  type HostedAgentWizardTab,
  validateHostedAgentForm,
} from "../utils/hosted-agents"

/** Tabs whose edits are collected into the single form Save. */
const FORM_TABS: HostedAgentWizardTab[] = [
  "profile",
  "instructions",
  "model",
  "tools",
  "libraries",
  "skills",
  "memory",
  "budget",
  "routines",
  "publish",
]

/**
 * Edit wizard for a hosted agent (HERMES Fas 5): Profile / Instructions /
 * Model / Tools / Skills / Memory / Budget form tabs saved as one
 * revision-guarded PUT, plus Access (separate policy endpoint), Test
 * (selftest through the real execution path) and Raw JSON (hash-guarded).
 *
 * Concurrency: the PUT carries the revision of the form's baseline; a
 * 409 revision_conflict surfaces an inline conflict alert with an explicit
 * "reload latest" choice (which discards local edits).
 */
export function EditHostedAgentDialog({
  agentId,
  isOpen,
  initialTab = "profile",
  onOpenChange,
}: {
  agentId: string | undefined
  isOpen: boolean
  /** Tab to open on; the table's lifecycle shortcut opens straight on "lifecycle". */
  initialTab?: HostedAgentWizardTab
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<HostedAgentWizardTab>(initialTab)
  const [form, setForm] = useState<HostedAgentFormState | null>(null)
  const [baseline, setBaseline] = useState<HostedAgent | null>(null)
  const [errors, setErrors] = useState<HostedAgentFormErrors>({})
  // "revision": someone else changed the agent (PUT 409).
  // "raw": a raw-JSON save changed the stored definition while the form has
  // unsaved edits — the admin chooses between reloading and keeping edits.
  const [conflict, setConflict] = useState<"revision" | "raw" | null>(null)
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)
  const [rawResetSignal, setRawResetSignal] = useState(0)
  const [sourceDirty, setSourceDirty] = useState(false)
  const [sourceBusy, setSourceBusy] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  const detailQuery = useAdminHostedAgentQuery(agentId, isOpen)
  const agent = detailQuery.data?.agent
  const modelsQuery = useAdminModelsQuery(isOpen)
  const toolCatalogQuery = useAdminHostedAgentToolCatalogQuery(isOpen)
  const rawQuery = useAdminHostedAgentRawQuery(agentId, isOpen)

  const updateMutation = useUpdateHostedAgentMutation()
  const accessMutation = useUpdateHostedAgentAccessMutation()
  const rawMutation = useSaveHostedAgentRawMutation()

  // Reset all wizard state when the dialog closes; initialize the form from
  // the first detail read after it opens. Later background refetches (e.g.
  // after an access save bumps the revision) must NOT clobber form edits —
  // the save keeps the form baseline unless only non-form fields changed.
  useEffect(() => {
    if (!isOpen) {
      setForm(null)
      setBaseline(null)
      setErrors({})
      setConflict(null)
      setIsAccessDialogOpen(false)
    } else {
      setActiveTab(initialTab)
    }
  }, [isOpen, initialTab])

  useEffect(() => {
    if (isOpen && agent && form === null) {
      setForm(buildHostedAgentFormState(agent))
      setBaseline(agent)
    }
  }, [isOpen, agent, form])

  const isDirty = useMemo(() => {
    if (!form || !baseline) return false
    return JSON.stringify(form) !== JSON.stringify(buildHostedAgentFormState(baseline))
  }, [form, baseline])
  // A policy-only update may advance the revision. Any changed form field
  // must instead retain the old revision, so saving cannot overwrite another
  // editor's work merely because a background refetch saw their new revision.
  useEffect(() => {
    if (
      agent &&
      baseline &&
      agent.revision !== baseline.revision &&
      JSON.stringify(buildHostedAgentFormState(agent)) ===
        JSON.stringify(buildHostedAgentFormState(baseline))
    ) {
      setBaseline(agent)
    }
  }, [agent, baseline])
  useRegisterAdminDirty(`hosted-agent-edit:${agentId ?? ""}`, isOpen && isDirty)

  const patch = (partial: Partial<HostedAgentFormState>) => {
    setForm((current) => (current ? { ...current, ...partial } : current))
  }

  const requestClose = (open: boolean) => {
    if (open) return onOpenChange(true)
    if (sourceBusy || updateMutation.isPending) return
    if (isDirty || sourceDirty) setConfirmDiscard(true)
    else onOpenChange(false)
  }

  const handleSave = async () => {
    if (!agentId || !form || !baseline) return
    const validation = validateHostedAgentForm(form)
    setErrors(validation)
    if (Object.keys(validation).length > 0) {
      const target = firstInvalidHostedAgentTab(validation)
      if (target) setActiveTab(target)
      toast.error(
        t("adminDashboard.hostedAgents.editDialog.validationError", {
          defaultValue: "Fix the highlighted fields before saving.",
        })
      )
      return
    }
    try {
      const saved = await updateMutation.mutateAsync({
        agentId,
        agent: buildHostedAgentSavePayload(baseline, form),
        revision: baseline.revision,
      })
      toast.success(
        t("adminDashboard.hostedAgents.editDialog.saveSuccess", {
          defaultValue: "Hosted agent updated",
        })
      )
      setBaseline(saved)
      setForm(buildHostedAgentFormState(saved))
      if (!sourceDirty) onOpenChange(false)
    } catch (error) {
      if (getHostedAgentConflict(error)?.code === "revision_conflict") {
        setConflict("revision")
      } else {
        toast.error(
          t("adminDashboard.hostedAgents.editDialog.saveError", {
            defaultValue: "Failed to update hosted agent",
          }),
          { description: error instanceof Error ? error.message : undefined }
        )
      }
    }
  }

  const handleReloadLatest = async () => {
    const fresh = await detailQuery.refetch()
    const freshAgent = fresh.data?.agent
    if (freshAgent) {
      setForm(buildHostedAgentFormState(freshAgent))
      setBaseline(freshAgent)
      setErrors({})
      setConflict(null)
    }
  }

  const rawHash = rawQuery.data?.hash ?? ""
  const isFormTab = FORM_TABS.includes(activeTab)
  const stepIndex = HOSTED_AGENT_STEPS.findIndex((step) => step.tabs.includes(activeTab))
  const nextStep = stepIndex >= 0 && stepIndex < 4 ? HOSTED_AGENT_STEPS[stepIndex + 1] : null

  return (
    <>
      <Dialog open={isOpen} onOpenChange={requestClose}>
        <DialogContent className="sm:max-w-[880px]">
          <DialogHeader>
            <DialogTitle>
              {t("adminDashboard.hostedAgents.editDialog.title", {
                defaultValue: "Edit hosted agent",
              })}{" "}
              {agent ? <code className="font-mono text-sm">{agent.slug}</code> : null}
            </DialogTitle>
            <DialogDescription>
              {t("adminDashboard.hostedAgents.editDialog.description", {
                defaultValue:
                  "Configure the agent's profile, behavior, tools and budget. Form changes apply together when you save.",
              })}
            </DialogDescription>
          </DialogHeader>

          {conflict ? (
            <HostedAgentEditConflictAlert
              conflict={conflict}
              onDismiss={() => setConflict(null)}
              onReload={() => void handleReloadLatest()}
            />
          ) : null}

          {detailQuery.isLoading || !form || !agent ? (
            <DialogBody>
              {detailQuery.isError ? (
                <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm">
                  {t("adminDashboard.hostedAgents.editDialog.loadError", {
                    defaultValue: "Failed to load the agent.",
                  })}
                </p>
              ) : (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Skeleton key={index} className="h-10 w-full" />
                  ))}
                </div>
              )}
            </DialogBody>
          ) : (
            <fieldset
              aria-busy={updateMutation.isPending}
              className="flex min-h-0 flex-1 flex-col border-0 p-0"
              disabled={updateMutation.isPending}
            >
              <Tabs
                className="flex min-h-0 flex-1 flex-col"
                value={activeTab}
                onValueChange={(value) => setActiveTab(value as HostedAgentWizardTab)}
              >
                <HostedAgentEditTabsList activeTab={activeTab} onSelect={setActiveTab} />

                <DialogBody className="pt-4">
                  <TabsContent className="mt-0" value="profile">
                    <HostedAgentProfileTab
                      agentId={agent.id}
                      errors={errors}
                      form={form}
                      patch={patch}
                    />
                  </TabsContent>
                  <TabsContent
                    forceMount
                    className="mt-0 data-[state=inactive]:hidden"
                    value="sources"
                  >
                    <HostedAgentSourcesTab
                      agentId={agent.id}
                      onBusyChange={setSourceBusy}
                      onDirtyChange={setSourceDirty}
                    />
                  </TabsContent>
                  <TabsContent className="mt-0" value="publish">
                    <HostedAgentPublishTab agent={agent} form={form} patch={patch} />
                  </TabsContent>
                  <TabsContent className="mt-0" value="instructions">
                    <HostedAgentInstructionsTab errors={errors} form={form} patch={patch} />
                  </TabsContent>
                  <TabsContent className="mt-0" value="model">
                    <HostedAgentModelTab
                      errors={errors}
                      form={form}
                      isLoadingModels={modelsQuery.isLoading}
                      models={modelsQuery.data ?? []}
                      patch={patch}
                    />
                  </TabsContent>
                  <TabsContent className="mt-0" value="tools">
                    <HostedAgentToolsTab
                      catalog={toolCatalogQuery.data ?? []}
                      catalogFailed={toolCatalogQuery.isError}
                      form={form}
                      isLoadingCatalog={toolCatalogQuery.isLoading}
                      patch={patch}
                    />
                  </TabsContent>
                  <TabsContent className="mt-0" value="libraries">
                    <HostedAgentLibrariesTab form={form} patch={patch} />
                  </TabsContent>
                  <TabsContent className="mt-0" value="skills">
                    <HostedAgentSkillsTab
                      agent={agent}
                      form={form}
                      patch={patch}
                      onInstalledChanged={() => detailQuery.refetch()}
                    />
                  </TabsContent>
                  <TabsContent className="mt-0" value="memory">
                    <HostedAgentMemoryTab errors={errors} form={form} patch={patch} />
                    {/* #3023: working state needs a saved agent (there is no id
                        to hang facts off before the first save), same gate as
                        the Test panel. */}
                    {agentId ? <HostedAgentWorkingStatePanel agentId={agentId} /> : null}
                  </TabsContent>
                  <TabsContent className="mt-0" value="budget">
                    <HostedAgentBudgetTab errors={errors} form={form} patch={patch} />
                    <HostedAgentUsagePanel
                      agentId={agent.id}
                      budgetStatus={detailQuery.data?.budget_status ?? null}
                    />
                  </TabsContent>

                  <TabsContent className="mt-0" value="routines">
                    <HostedAgentRoutinesTab
                      agentId={agentId}
                      errors={errors}
                      form={form}
                      patch={patch}
                    />
                  </TabsContent>

                  <TabsContent className="mt-0" value="access">
                    <HostedAgentAccessTab
                      agent={agent}
                      onEditPolicy={() => setIsAccessDialogOpen(true)}
                    />
                  </TabsContent>

                  <TabsContent className="mt-0" value="test">
                    <p className="text-muted-foreground mb-4 text-sm">
                      {t("adminDashboard.hostedAgents.product.testHelp")}
                    </p>
                    {agentId ? <HostedAgentSelftestPanel agentId={agentId} /> : null}
                  </TabsContent>

                  <TabsContent className="mt-0" value="evals">
                    <HostedAgentEvalsTab agentId={agentId} />
                  </TabsContent>

                  <TabsContent className="mt-0" value="lifecycle">
                    {agentId ? (
                      <HostedAgentLifecyclePanel
                        agentId={agentId}
                        agentName={agent.profile.name}
                        currentVersion={detailQuery.data?.hermes_version.last_seen ?? null}
                      />
                    ) : null}
                  </TabsContent>

                  <TabsContent className="mt-0" value="raw">
                    <RawJsonTab
                      confirmToken={agent.slug}
                      isPending={rawMutation.isPending || !rawHash}
                      resetSignal={rawResetSignal}
                      value={rawQuery.data?.entity ?? {}}
                      resourceLabel={t("adminDashboard.hostedAgents.rawTab.resourceLabel", {
                        defaultValue: "hosted agent {{slug}}",
                        slug: agent.slug,
                      })}
                      onSave={async (next, reason) => {
                        if (!agentId) return
                        try {
                          await rawMutation.mutateAsync({
                            agentId,
                            entity: next as Record<string, unknown>,
                            expectedHash: rawHash,
                            reason,
                            source: "raw",
                          })
                          toast.success(
                            t("adminDashboard.hostedAgents.rawTab.saveSuccess", {
                              defaultValue: "Raw definition saved",
                            })
                          )
                          setRawResetSignal((signal) => signal + 1)
                          // The stored definition changed server-side. With a
                          // pristine form, silently refresh the baseline; with
                          // unsaved form edits, refetch only the revision
                          // baseline and let the admin choose (never silently
                          // discard their edits).
                          if (isDirty) {
                            await detailQuery.refetch()
                            setConflict("raw")
                          } else {
                            await handleReloadLatest()
                          }
                        } catch (error) {
                          if (getHostedAgentConflict(error)?.code === "hash_mismatch") {
                            await rawQuery.refetch()
                            throw new Error(
                              t("adminDashboard.hostedAgents.rawTab.hashMismatch", {
                                defaultValue:
                                  "The agent changed on the server — the editor has been reloaded with the latest version. Re-apply your change and save again.",
                              })
                            )
                          }
                          throw error
                        }
                      }}
                    />
                  </TabsContent>
                </DialogBody>

                {(isFormTab || nextStep) && (
                  <HostedAgentEditorFooter
                    nextStep={nextStep}
                    pending={updateMutation.isPending || sourceBusy}
                    onClose={() => requestClose(false)}
                    onSave={() => void handleSave()}
                    onStep={setActiveTab}
                  />
                )}
              </Tabs>
            </fieldset>
          )}
        </DialogContent>
      </Dialog>

      <HostedAgentDiscardDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        onDiscard={() => {
          setConfirmDiscard(false)
          onOpenChange(false)
        }}
      />
      <AccessPolicyDialog
        isSaving={accessMutation.isPending}
        open={isAccessDialogOpen}
        policy={agent?.access}
        description={t("adminDashboard.hostedAgents.accessDescription", {
          defaultValue: "Control which users and groups can see and use this agent.",
        })}
        title={t("adminDashboard.access.dialogTitle", {
          defaultValue: "Access policy — {{name}}",
          name: agent?.profile.name ?? "",
        })}
        onOpenChange={setIsAccessDialogOpen}
        onSave={async (policy) => {
          if (!agentId) return
          try {
            await accessMutation.mutateAsync({ agentId, policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              getHostedAgentConflict(error)?.code === "revision_conflict"
                ? t("adminDashboard.hostedAgents.revisionConflict", {
                    defaultValue: "The agent changed while you were editing — reload and retry.",
                  })
                : t("adminDashboard.access.saveError", {
                    defaultValue: "Failed to update access policy",
                  })
            )
            throw error
          }
        }}
      />
    </>
  )
}
